import axios from 'axios'

const TOKEN_KEY = 'mpg_token'
const IMPERSONATION_KEY = 'mpg_impersonation_token'
const IMPERSONATION_RETURN_KEY = 'mpg_impersonation_return'

/** Where the admin lands when a session started before the return path was stored ends. */
const DEFAULT_RETURN = '/admin/staff'

/**
 * An admin "signed in as" a staff member or a customer holds that account's
 * token here, per tab: sessionStorage survives a reload but dies with the tab
 * and is not shared with other tabs, so the admin's own token stays untouched
 * in localStorage and every other tab keeps showing the admin console.
 *
 * The return path travels with the token: the admin came from Staff or from
 * Customers, and both the explicit return and the 401 fallback below need to
 * know which without a user object in hand.
 */
export const impersonationStore = {
  get: () => {
    try {
      return sessionStorage.getItem(IMPERSONATION_KEY)
    } catch {
      return null
    }
  },
  returnTo: () => {
    try {
      return sessionStorage.getItem(IMPERSONATION_RETURN_KEY) ?? DEFAULT_RETURN
    } catch {
      return DEFAULT_RETURN
    }
  },
  set: (token: string, returnTo: string) => {
    try {
      sessionStorage.setItem(IMPERSONATION_KEY, token)
      sessionStorage.setItem(IMPERSONATION_RETURN_KEY, returnTo)
    } catch {
      // storage blocked - the switch simply does not stick across a reload
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(IMPERSONATION_KEY)
      sessionStorage.removeItem(IMPERSONATION_RETURN_KEY)
    } catch {
      // nothing stored
    }
  },
}

export const tokenStore = {
  /** The impersonation token wins while one is set for this tab. */
  get: () => impersonationStore.get() ?? localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
}

// Same-origin by default: nginx proxies /api to the backend service in
// production, and Vite proxies it to php artisan serve in development (see
// vite.config.ts). That keeps this image environment-independent - the same
// build runs anywhere - and means there is no CORS to configure.
// VITE_API_URL still overrides it for pointing at a remote API.
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v1',
  headers: { Accept: 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = tokenStore.get()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      // Only the request that used the token we currently hold may act on a
      // 401. Several requests are usually in flight; after the first one has
      // switched tokens below, a second stale 401 must not clear the token
      // that just replaced it and bounce the admin to the sign-in page.
      const sent = error.config?.headers?.Authorization
      const current = tokenStore.get()
      if (current && sent !== `Bearer ${current}`) {
        return Promise.reject(error)
      }
      if (impersonationStore.get()) {
        // The "sign in as" token expired or was revoked: drop it and carry
        // on as the admin, whose own token is still in localStorage, back on
        // the screen the admin started from.
        const returnTo = impersonationStore.returnTo()
        impersonationStore.clear()
        window.location.assign(returnTo)
      } else {
        tokenStore.clear()
        window.location.assign('/login')
      }
    }
    return Promise.reject(error)
  },
)

export function fieldErrors(error: unknown): Record<string, string[]> {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.errors ?? {}
  }
  return {}
}

export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (data?.errors) {
      const first = Object.values(data.errors as Record<string, string[]>)[0]
      if (first?.[0]) return first[0]
    }
    if (data?.message) return data.message
  }
  return 'Something went wrong. Please try again.'
}
